import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useSearchParams } from "react-router-dom";
import { ActionButton } from "../../components/Buttons";
import { useAnalyticsConsent } from "../../contexts/AnalyticsConsentContext";
import { trackMixpanelEvent } from "../../utils/Analytics";
import { ensureAnonymousAuth } from "../../utils/auth/anonymousAuth";
import { getCardById } from "../../utils/firestore";
import {
  CARD_MODERATION_STATUS,
  CARD_STATUS,
  CARD_VISIBILITY,
  PARTNER_WALL_CARD_SCHEMA_VERSION,
} from "../../utils/cards/cardConstants";
import {
  getLanguageText,
} from "../../utils/cards/normalizeWallCard";
import { getCardLanguagePresentation } from "../../utils/cards/languageAdaptiveCard";
import { getValidShareId } from "../../utils/cards/shareAttribution";
import BackgroundLights from "./components/BackgroundLights";
import EmpathyPageHeader from "./components/EmpathyPageHeader";
import RefinedCardPreview from "./components/RefinedCardPreview";
import ShareCardPreviewView from "./components/ShareCardPreviewView";
import usePreparedCardShare from "./usePreparedCardShare";

const SHARED_CARD_DARK_BACKGROUND = "#050604";
function isPublicSharedCard(card) {
  const isLegacySharedCard = (
    card?.schemaVersion === 2 &&
    card.status === CARD_STATUS.PUBLISHED &&
    (card.visibility === CARD_VISIBILITY.SHARED_LINK || card.isShared)
  );

  const isPublicV3Card = (
    card?.schemaVersion === PARTNER_WALL_CARD_SCHEMA_VERSION &&
    card.visibility === CARD_VISIBILITY.PUBLIC &&
    card.moderationStatus === CARD_MODERATION_STATUS.APPROVED &&
    card.archivedAt === null
  );

  return isLegacySharedCard || isPublicV3Card;
}

function SharedCardView() {
  const { cardId } = useParams();
  const [searchParams] = useSearchParams();
  const referredByShareId = getValidShareId(searchParams.get("shareId"));
  const [card, setCard] = useState(null);
  const [loadedCardId, setLoadedCardId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const trackedViewedCardKeysRef = useRef(new Set());
  const { analyticsReady, mixpanelReady } = useAnalyticsConsent();
  const {
    closeSharePreview,
    copyShareLink,
    openShareSheet,
    prepareShare,
    resetShareState,
    shareError,
    sharePreview,
    sharing,
    updateSharePreviewMessage,
  } = usePreparedCardShare({
    missingCardMessage: "We couldn't find this card to share.",
    referredByShareId,
    source: "Shared card",
  });

  useLayoutEffect(() => {
    const rootElement = document.getElementById("root");
    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    let statusBarMeta = document.querySelector(
      'meta[name="apple-mobile-web-app-status-bar-style"]'
    );
    const createdThemeColorMeta = !themeColorMeta;
    const createdStatusBarMeta = !statusBarMeta;
    const previousHtmlBackground = document.documentElement.style.backgroundColor;
    const previousBodyBackground = document.body.style.backgroundColor;
    const previousRootBackground = rootElement?.style.backgroundColor || "";
    const previousThemeColor = themeColorMeta?.getAttribute("content") || "";
    const previousStatusBarStyle =
      statusBarMeta?.getAttribute("content") || "";

    if (!themeColorMeta) {
      themeColorMeta = document.createElement("meta");
      themeColorMeta.setAttribute("name", "theme-color");
      document.head.appendChild(themeColorMeta);
    }

    if (!statusBarMeta) {
      statusBarMeta = document.createElement("meta");
      statusBarMeta.setAttribute(
        "name",
        "apple-mobile-web-app-status-bar-style"
      );
      document.head.appendChild(statusBarMeta);
    }

    document.documentElement.style.backgroundColor = SHARED_CARD_DARK_BACKGROUND;
    document.body.style.backgroundColor = SHARED_CARD_DARK_BACKGROUND;
    themeColorMeta.setAttribute("content", SHARED_CARD_DARK_BACKGROUND);
    statusBarMeta.setAttribute("content", "black");

    if (rootElement) {
      rootElement.style.backgroundColor = SHARED_CARD_DARK_BACKGROUND;
    }

    return () => {
      document.documentElement.style.backgroundColor = previousHtmlBackground;
      document.body.style.backgroundColor = previousBodyBackground;

      if (rootElement) {
        rootElement.style.backgroundColor = previousRootBackground;
      }

      if (createdThemeColorMeta) {
        themeColorMeta.remove();
      } else {
        themeColorMeta.setAttribute("content", previousThemeColor);
      }

      if (createdStatusBarMeta) {
        statusBarMeta.remove();
      } else {
        statusBarMeta.setAttribute("content", previousStatusBarStyle);
      }
    };
  }, []);

  useEffect(() => {
    setCard(null);
    setLoadedCardId("");
    setLoading(true);
    setError("");
    resetShareState({ clearPreparedUrls: true });
    let isMounted = true;

    const loadCard = async () => {
      try {
        await ensureAnonymousAuth();
        const nextCard = await getCardById(cardId);

        if (!isMounted) return;

        if (!isPublicSharedCard(nextCard)) {
          setError("This card is not available.");
          return;
        }

        setCard(nextCard);
        setLoadedCardId(cardId);
      } catch (loadError) {
        if (!isMounted) return;

        console.error("Unable to load shared card", loadError);
        setError("We couldn't open this card yet.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCard();

    return () => {
      isMounted = false;
    };
  }, [cardId, resetShareState]);

  useEffect(() => {
    if (
      !analyticsReady ||
      !mixpanelReady ||
      !card ||
      loadedCardId !== cardId
    ) {
      return;
    }

    const viewedCardKey = `${cardId}:${referredByShareId || ""}`;
    if (trackedViewedCardKeysRef.current.has(viewedCardKey)) return;

    const tracked = trackMixpanelEvent("Received Card Viewed", {
      card_id: cardId,
      share_id: referredByShareId || undefined,
      entry_point: "friend_share",
      user_role: "recipient",
    });

    if (tracked) {
      trackedViewedCardKeysRef.current.add(viewedCardKey);
    }
  }, [
    analyticsReady,
    card,
    cardId,
    loadedCardId,
    mixpanelReady,
    referredByShareId,
  ]);

  const question =
    getLanguageText(card, card?.languageOrder?.[0] || "en") ||
    card?.questionRefined ||
    "";
  const languagePresentation = getCardLanguagePresentation(
    {
      ...card,
      questionRefined: question,
    },
    {}
  );
  const canReshare =
    card?.schemaVersion === PARTNER_WALL_CARD_SCHEMA_VERSION && Boolean(question);
  const createCardPath = referredByShareId
    ? `/refine?shareId=${encodeURIComponent(referredByShareId)}`
    : "/refine";
  const shareCard = async () => {
    await prepareShare({ cardId, question });
  };

  return (
    <div className="overflow-hidden bg-[#050604] px-5 py-8 text-center font-sans text-white sm:px-8 sm:py-12">
      <Helmet>
        <meta name="theme-color" content={SHARED_CARD_DARK_BACKGROUND} />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      </Helmet>

      <div className="relative mx-auto max-w-4xl flex flex-col">
        <EmpathyPageHeader title="LettuceTalk" showAccountMenu={false} />

        <main className="relative flex flex-1 flex-col items-center justify-center py-4">
          <div className="pointer-events-none absolute inset-x-[-18%] top-[30%]">
            <BackgroundLights />
          </div>

          <h2 className="relative z-10 mx-auto max-w-sm text-balance font-serif text-xl text-white/68 sm:max-w-md sm:text-4xl">
            Someone shared this LettuceTalk card with you.
          </h2>

          <div className="relative z-10 mt-4 flex w-full justify-center sm:mt-8">
            {loading ? (
              <div className="flex min-h-[390px] items-center justify-center text-sm font-bold text-white/60">
                Opening card...
              </div>
            ) : error ? (
              <div className="mx-auto max-w-md rounded-2xl border border-white/10 bg-white/[0.06] px-5 py-6 text-sm font-bold text-white/70 shadow-[0_20px_70px_rgba(0,0,0,0.28)] backdrop-blur-xl">
                {error}
              </div>
            ) : (
              <RefinedCardPreview
                result={{
                  refinedQuestion: question,
                  primaryLanguage: languagePresentation.primaryLanguage,
                  primaryLanguageCode: languagePresentation.primaryLanguageCode,
                  translation: languagePresentation.translation,
                  translations: languagePresentation.translations,
                }}
                size="large"
              />
            )}
          </div>

          <div className="relative z-10 mt-6 flex w-full max-w-sm flex-col gap-3">
            <ActionButton
              to={createCardPath}
              fullWidth
              icon="plus"
              onClick={() =>
                trackMixpanelEvent("Recipient Creation Started", {
                  card_id: cardId,
                  share_id: referredByShareId,
                  entry_point: "friend_share",
                  user_role: "recipient",
                })
              }
            >
              Create a Card for Someone
            </ActionButton>

            {canReshare ? (
              <ActionButton
                type="button"
                onClick={shareCard}
                disabled={sharing}
                fullWidth
                icon="share"
                variant="secondary"
              >
                {sharing ? "Preparing Your Link" : "Share this card"}
              </ActionButton>
            ) : null}
          </div>

          {!sharePreview && shareError ? (
            <p className="relative z-10 mt-3 text-sm font-bold text-white/70">
              {shareError}
            </p>
          ) : null}

          <p className="relative z-10 mx-auto mt-4 max-w-lg text-sm text-balance text-gray-200 sm:text-lg">
            Have something hard to ask? LettuceTalk helps you say it gently.
          </p>
        </main>
      </div>

      {sharePreview ? (
        <ShareCardPreviewView
          selectedIntroId={sharePreview.selectedIntroId}
          cardUrl={sharePreview.cardUrl}
          isPreparing={sharePreview.isPreparing}
          preparationFailed={sharePreview.preparationFailed}
          shareError={shareError}
          onMessageChange={updateSharePreviewMessage}
          onOpenShareSheet={openShareSheet}
          onPrepareShareLink={shareCard}
          onCopyLink={copyShareLink}
          onClose={closeSharePreview}
        />
      ) : null}
    </div>
  );
}

export default SharedCardView;
